"use client";

import { useMemo } from "react";
import { useCalendarContext } from "@/lib/calendar-context";

export interface MeetingAggregatesParams {
  start: Date;
  end: Date;
  email?: string; // optional, defaults to current selected person in context
}

export function useMeetingAggregates({ start, end, email }: MeetingAggregatesParams) {
  const { events, selectedPerson, setSelectedPerson } = useCalendarContext();

  // Optionally scope to a specific email by temporarily selecting it in context
  // Consumers should set once per page based on viewed employee; we avoid side effects here.
  const effectiveEmail = email || selectedPerson;

  const aggregates = useMemo(() => {
    const startMs = start.getTime();
    const endMs = end.getTime();
    let count = 0;
    let totalMinutes = 0;

    for (const e of events) {
      if (effectiveEmail && e.source && e.source !== effectiveEmail) continue;
      const s = new Date(e.start).getTime();
      if (s < startMs || s > endMs) continue;
      count += 1;
      const endTime = e.end ? new Date(e.end).getTime() : s;
      const minutes = Math.max(0, Math.round((endTime - s) / (1000 * 60)));
      totalMinutes += minutes;
    }

    return { count, totalMinutes };
  }, [events, start, end, effectiveEmail]);

  return aggregates;
}
